import * as React from 'react';
import { cn } from '@/lib/utils';

export function Panel({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <section className={cn('panel', className)} {...props} />;
}

export interface PanelHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  title: string;
  /** A short count or state, set in mono beside the title. */
  meta?: React.ReactNode;
  actions?: React.ReactNode;
}

export function PanelHeader({ title, meta, actions, className, children, ...props }: PanelHeaderProps) {
  return (
    <div
      className={cn('flex min-h-[38px] items-center justify-between gap-3 border-b border-rule px-3 py-2', className)}
      {...props}
    >
      <div className="flex min-w-0 items-baseline gap-2.5">
        <h2 className="eyebrow text-ink">{title}</h2>
        {meta ? <span className="truncate font-mono text-tiny text-ink-3">{meta}</span> : null}
      </div>
      {actions ? <div className="flex shrink-0 items-center gap-1.5">{actions}</div> : null}
      {children}
    </div>
  );
}

export function PanelBody({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('p-3', className)} {...props} />;
}

/** A key → value row. Stacked, these read as the console's gauge cluster: the
    facts a reviewer checks before acting, in a fixed order, always mono on the
    right so a changed value is visible without reading. */
export function Fact({
  label,
  children,
  className,
  title,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
  title?: string;
}) {
  return (
    <div className={cn('flex items-baseline justify-between gap-3 border-b border-rule py-1.5 last:border-b-0', className)}>
      <span className="eyebrow shrink-0">{label}</span>
      <span className="min-w-0 truncate text-right font-mono text-xs text-ink" title={title}>
        {children}
      </span>
    </div>
  );
}
